import { plainToClass } from "class-transformer";
import { produce } from "immer";
import { createContext, PropsWithChildren, useCallback, use, useMemo, useState } from "react";
import UserRest from "../../api/model/UserRest";
import User from "./User";

export type UpdateUser = (updateUserFunction: (draft: UserRest) => void) => User | undefined;
export type updateIsUserFetched = (isFetched: boolean) => void;

export const UserContext = createContext<{ user: UserRest; isFetched: boolean } | undefined>(
  undefined,
);
export const UserUpdateContext = createContext<UpdateUser | undefined>(undefined);
export const IsFetchedUpdateContext = createContext<updateIsUserFetched | undefined>(undefined);

const DEFAULT_USER = plainToClass(UserRest, { personalDetails: {} });

const UserProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<{ user: UserRest; isFetched: boolean }>({
    user: DEFAULT_USER,
    isFetched: false,
  });

  const updateUser = useCallback(
    (updateUserFunction: any) => {
      const newUser = produce(state.user, updateUserFunction);
      setState((prevState) => ({
        ...prevState,
        user: newUser,
        isFetched: true,
      }));

      return new User(newUser, updateUserFunction);
    },
    [state.user],
  );

  const updateIsUserFetched = useCallback((isFetched: boolean) => {
    setState((prevState) => ({
      ...prevState,
      isFetched,
    }));
  }, []);

  return (
    <UserContext.Provider value={state}>
      <UserUpdateContext.Provider value={updateUser}>
        <IsFetchedUpdateContext.Provider value={updateIsUserFetched}>
          {children}
        </IsFetchedUpdateContext.Provider>
      </UserUpdateContext.Provider>
    </UserContext.Provider>
  );
};

const useUserState = () => {
  const context = use(UserContext);
  if (!context) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  const { user, isFetched } = context;
  const updateUser = use(UserUpdateContext)!;
  const userInstance = useMemo(
    () => (user ? new User(user, updateUser) : undefined),
    [user, updateUser],
  );
  if (!userInstance) {
    throw new Error("useUserState must be used within a UserProvider");
  }

  return { user: userInstance, isFetched };
};

const useUserUpdate = () => {
  const updateUser = use(UserUpdateContext);
  if (!updateUser) {
    throw new Error("useUserUpdate must be used within a UserProvider");
  }

  return updateUser;
};

const useIsUserFetchedUpdate = () => {
  const updateIsUserFetched = use(IsFetchedUpdateContext);
  if (!updateIsUserFetched) {
    throw new Error("useIsUserFetchedUpdate must be used within a UserProvider");
  }

  return updateIsUserFetched;
};

const useUser: () => {
  user: User;
  updateUser: UpdateUser;
  updateIsUserFetched: updateIsUserFetched;
  isSignedIn: boolean;
  isFetched: boolean;
} = () => {
  const { user, isFetched } = useUserState();
  return {
    user,
    isSignedIn: user.isLoggedIn,
    updateUser: useUserUpdate(),
    updateIsUserFetched: useIsUserFetchedUpdate(),
    isFetched,
  };
};

export { UserProvider, useUser };
